/**
 * 移动端订单列表页（带多选导出功能）
 */
import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Tabs,
  PullToRefresh,
  Empty,
  Skeleton,
  Checkbox,
  Button,
  Dialog,
  Toast,
  SearchBar,
  Input,
  DatePicker,
} from 'antd-mobile';
import { adminOrderApi, adminPackingSlipApi } from '../../api/index';
import dayjs from 'dayjs';

// Tab 配置（与桌面端对齐）
const TAB_ITEMS = [
  { key: 'all', title: '全部', status: undefined, financialStatus: undefined, isDraft: undefined },
  { key: 'pending_shipment', title: '待发货', status: 'paid', financialStatus: undefined, isDraft: false, filterFn: (o) => o.financialStatus === 'paid' || o.financialStatus === 'partially_refunded' },
  { key: 'completed', title: '已完成', status: undefined, financialStatus: undefined, isDraft: false, filterFn: (o) => o.status === 'shipped' || o.status === 'completed' },
  { key: 'cancelled', title: '已取消', status: 'cancelled', financialStatus: undefined, isDraft: undefined },
];

// 格式化时间（显示时分秒）
const formatDateTime = (dateStr) => {
  if (!dateStr) return '-';
  const date = new Date(dateStr);
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  const seconds = String(date.getSeconds()).padStart(2, '0');
  return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
};

export default function MobileOrderListPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [orders, setOrders] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(20);
  const [totalPages, setTotalPages] = useState(1);
  const [activeTab, setActiveTab] = useState('all');
  const [selectedOrderIds, setSelectedOrderIds] = useState([]);
  const [exporting, setExporting] = useState(false);
  const [showSelectMode, setShowSelectMode] = useState(false);
  const [keyword, setKeyword] = useState('');
  const [searchKeyword, setSearchKeyword] = useState('');
  const [pageLoading, setPageLoading] = useState(false);

  // 导出报告弹窗状态
  const [exportReportVisible, setExportReportVisible] = useState(false);
  const [exportStartDate, setExportStartDate] = useState(dayjs().subtract(7, 'day').toDate());
  const [exportEndDate, setExportEndDate] = useState(dayjs().toDate());
  const [exportReportLoading, setExportReportLoading] = useState(false);

  // 发货弹窗状态
  const [shipModalVisible, setShipModalVisible] = useState(false);
  const [shippingOrder, setShippingOrder] = useState(null);
  const [trackingCompany, setTrackingCompany] = useState('');
  const [trackingNumber, setTrackingNumber] = useState('');
  const [shipping, setShipping] = useState(false);

  // 获取当前 Tab 配置
  const getCurrentTabConfig = useCallback(() => {
    return TAB_ITEMS.find((t) => t.key === activeTab) || TAB_ITEMS[0];
  }, [activeTab]);

  useEffect(() => {
    fetchOrders(1);
  }, [activeTab, searchKeyword]);

  const fetchOrders = async (pageNum, isPageChange = false) => {
    if (isPageChange) {
      setPageLoading(true);
    } else {
      setLoading(true);
    }
    try {
      const tabConfig = getCurrentTabConfig();

      const data = await adminOrderApi.list({
        keyword: searchKeyword || undefined,
        status: tabConfig.status,
        financialStatus: tabConfig.financialStatus,
        isDraft: tabConfig.isDraft,
        page: pageNum,
        pageSize: pageSize,
      });

      let ordersList = [];
      let totalCount = 0;
      if (Array.isArray(data)) {
        ordersList = data;
        totalCount = data.length;
      } else {
        ordersList = data?.items || data?.list || [];
        totalCount = data?.total || ordersList.length;
      }

      // 应用客户端筛选（针对复合条件）
      if (tabConfig.filterFn) {
        ordersList = ordersList.filter(tabConfig.filterFn);
      }

      setOrders(ordersList);
      setTotal(totalCount);
      setTotalPages(Math.ceil(totalCount / pageSize));
      setPage(pageNum);
    } catch (err) {
      Toast.show('获取订单列表失败');
    } finally {
      setLoading(false);
      setPageLoading(false);
    }
  };

  // 搜索处理
  const handleSearch = () => {
    setSearchKeyword(keyword);
    setSelectedOrderIds([]);
  };

  // 翻页处理
  const handlePrevPage = () => {
    if (page > 1) {
      fetchOrders(page - 1, true);
      setSelectedOrderIds([]);
    }
  };

  const handleNextPage = () => {
    if (page < totalPages) {
      fetchOrders(page + 1, true);
      setSelectedOrderIds([]);
    }
  };

  const refreshData = async () => {
    await fetchOrders(1, false);
    setSelectedOrderIds([]);
  };

  // 切换选择模式
  const toggleSelectMode = () => {
    setShowSelectMode(!showSelectMode);
    setSelectedOrderIds([]);
  };

  // 选择/取消选择订单
  const toggleSelectOrder = (orderId) => {
    if (selectedOrderIds.includes(orderId)) {
      setSelectedOrderIds(selectedOrderIds.filter((id) => id !== orderId));
    } else {
      setSelectedOrderIds([...selectedOrderIds, orderId]);
    }
  };

  // 全选/取消全选
  const toggleSelectAll = () => {
    if (selectedOrderIds.length === orders.filter((o) => o.status !== 'cancelled').length) {
      setSelectedOrderIds([]);
    } else {
      setSelectedOrderIds(orders.filter((o) => o.status !== 'cancelled').map((o) => o.id));
    }
  };

  // 导出装箱单
  const handleExportPackingSlip = async () => {
    if (selectedOrderIds.length === 0) {
      Toast.show('请选择至少一个订单');
      return;
    }
    setExporting(true);
    try {
      const blob = await adminPackingSlipApi.print(selectedOrderIds);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const dateStr = new Date().toISOString().slice(0, 10);
      link.download = `装箱单_${dateStr}_${selectedOrderIds.length}单.pdf`;
      link.click();
      window.URL.revokeObjectURL(url);
      Toast.show('装箱单已导出');
      setShowSelectMode(false);
      setSelectedOrderIds([]);
    } catch (err) {
      Toast.show('导出失败：' + (err?.response?.data?.message || '未知错误'));
    } finally {
      setExporting(false);
    }
  };

  // 确认导出
  const confirmExport = () => {
    Dialog.confirm({
      content: `确定要导出 ${selectedOrderIds.length} 个订单的装箱单吗？`,
      onConfirm: handleExportPackingSlip,
    });
  };

  // 导出订单报告
  const handleExportReport = async () => {
    const startDate = dayjs(exportStartDate).format('YYYY-MM-DD');
    const endDate = dayjs(exportEndDate).format('YYYY-MM-DD');
    setExportReportLoading(true);
    try {
      const blob = await adminOrderApi.exportReport(startDate, endDate);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `订单报告_${startDate}_${endDate}.csv`;
      link.click();
      window.URL.revokeObjectURL(url);
      Toast.show('订单报告已导出');
      setExportReportVisible(false);
    } catch (err) {
      Toast.show('导出失败：' + (err?.response?.data?.message || '未知错误'));
    } finally {
      setExportReportLoading(false);
    }
  };

  // 打开发货弹窗
  const openShipModal = (order) => {
    setShippingOrder(order);
    setTrackingCompany('SF'); // 默认顺丰
    setTrackingNumber('');
    setShipModalVisible(true);
  };

  // 执行发货
  const handleShip = async () => {
    if (!shippingOrder) return;
    setShipping(true);
    try {
      await adminOrderApi.ship(shippingOrder.id, {
        trackingCompany: trackingCompany || undefined,
        trackingNumber: trackingNumber || undefined,
      });
      Toast.show('发货成功');
      setShipModalVisible(false);
      refreshData();
    } catch (err) {
      Toast.show(err?.response?.data?.message || '发货失败');
    } finally {
      setShipping(false);
    }
  };

  if (loading && orders.length === 0) {
    return (
      <div style={{ padding: 16 }}>
        <Skeleton.Title rows={3} />
        <Skeleton.Title rows={5} style={{ marginTop: 16 }} />
        <Skeleton.Title rows={5} style={{ marginTop: 16 }} />
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', background: '#f5f5f5' }}>
      {/* 搜索栏 */}
      <div style={{ background: '#fff', padding: '6px 12px' }}>
        <SearchBar
          value={keyword}
          onChange={setKeyword}
          onSearch={handleSearch}
          placeholder="搜索订单号或客户邮箱"
          style={{ '--background': '#f5f5f5' }}
        />
      </div>

      {/* 顶部操作栏 */}
      <div style={{ background: '#fff', padding: '8px 12px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        {showSelectMode ? (
          <>
            <Button size="small" variant="outline" onClick={() => setSelectedOrderIds([])}>
              清空
            </Button>
            <div style={{ fontSize: 14 }}>
              已选 {selectedOrderIds.length} 个订单
            </div>
            <Button size="small" color="primary" onClick={toggleSelectMode}>
              完成
            </Button>
          </>
        ) : (
          <>
            <Button size="small" variant="outline" onClick={toggleSelectMode}>
              多选
            </Button>
            <div style={{ fontSize: 14, color: '#666' }}>
              {activeTab === 'all' ? '全部订单' : TAB_ITEMS.find((t) => t.key === activeTab)?.title}
            </div>
            <Button size="small" color="primary" onClick={() => setExportReportVisible(true)}>
              导出报告
            </Button>
          </>
        )}
      </div>

      {/* 状态筛选 Tabs */}
      <Tabs
        activeKey={activeTab}
        onChange={(key) => {
          setActiveTab(key);
          setSelectedOrderIds([]);
        }}
        style={{
          '--title-font-size': '15px',
          '--active-title-color': '#1677ff',
          '--active-line-color': '#1677ff',
        }}
      >
        {TAB_ITEMS.map((tab) => (
          <Tabs.Tab key={tab.key} title={tab.title} />
        ))}
      </Tabs>

      {/* 订单列表 */}
      <PullToRefresh onRefresh={refreshData}>
        {orders.length > 0 ? (
          <>
            {/* 全选按钮（仅多选模式显示） */}
            {showSelectMode && (
              <div
                style={{
                  background: '#fff',
                  padding: '8px 12px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  borderBottom: '1px solid #f0f0f0',
                }}
                onClick={toggleSelectAll}
              >
                <Checkbox
                  checked={selectedOrderIds.length === orders.filter((o) => o.status !== 'cancelled').length && orders.filter((o) => o.status !== 'cancelled').length > 0}
                  onChange={toggleSelectAll}
                />
                <span style={{ fontSize: 14 }}>全选</span>
              </div>
            )}

            {/* 自定义订单卡片列表 */}
            <div style={{ padding: '8px 12px' }}>
              {orders.map((order) => {
                const isDisabled = order.status === 'cancelled';
                const isChecked = selectedOrderIds.includes(order.id);
                const canShip = order.financialStatus === 'paid' || order.financialStatus === 'partially_refunded';

                return (
                  <OrderCard
                    key={order.id}
                    order={order}
                    isDisabled={isDisabled}
                    isChecked={isChecked}
                    canShip={canShip}
                    showSelectMode={showSelectMode}
                    onNavigate={() => navigate(`/admin/m/orders/${order.id}`)}
                    onToggleSelect={() => toggleSelectOrder(order.id)}
                    onOpenShipModal={() => openShipModal(order)}
                  />
                );
              })}
            </div>

            {/* 导出按钮（仅多选模式且有选中时显示） */}
            {showSelectMode && selectedOrderIds.length > 0 && (
              <div style={{ padding: 16, position: 'sticky', bottom: 0, background: '#f5f5f5' }}>
                <Button
                  block
                  color="primary"
                  size="large"
                  loading={exporting}
                  onClick={confirmExport}
                >
                  导出装箱单 ({selectedOrderIds.length})
                </Button>
              </div>
            )}
          </>
        ) : (
          <div style={{ padding: '80px 0' }}>
            <Empty description="暂无订单" />
          </div>
        )}
      </PullToRefresh>

      {/* 分页控件 */}
      {total > 0 && (
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          gap: 16,
          padding: '12px 16px',
          background: '#fff',
          position: 'sticky',
          bottom: 0,
          borderTop: '1px solid #f0f0f0',
        }}>
          <Button
            size="small"
            disabled={page <= 1 || pageLoading}
            onClick={handlePrevPage}
          >
            上一页
          </Button>
          <span style={{ fontSize: 14, color: '#666' }}>
            {pageLoading ? '加载中...' : `${page} / ${totalPages}`}
          </span>
          <Button
            size="small"
            disabled={page >= totalPages || pageLoading}
            onClick={handleNextPage}
          >
            下一页
          </Button>
        </div>
      )}

      {/* 发货弹窗 */}
      <Dialog
        visible={shipModalVisible}
        style={{ '--content-width': '90%' }}
        content={
          <div style={{ padding: '16px 0' }}>
            <h3 style={{ fontSize: 16, fontWeight: 600, marginBottom: 16, textAlign: 'center' }}>
              发货确认
            </h3>

            {shippingOrder && (
              <div style={{ marginBottom: 16, padding: 12, background: '#f5f5f5', borderRadius: 8 }}>
                <div style={{ fontSize: 14, fontWeight: 500 }}>订单号：{shippingOrder.orderNo}</div>
                <div style={{ fontSize: 13, color: '#666', marginTop: 4 }}>
                  收件人：{[shippingOrder.shippingLastName, shippingOrder.shippingFirstName].filter(Boolean).join(' ') || '-'}
                </div>
                <div style={{ fontSize: 13, color: '#666', marginTop: 2 }}>
                  金额：${shippingOrder.total?.toFixed(2) || '0.00'}
                </div>
              </div>
            )}

            <div style={{ marginBottom: 12 }}>
              <div style={{ fontSize: 14, marginBottom: 4 }}>物流公司（可选）</div>
              <Input
                value={trackingCompany}
                onChange={setTrackingCompany}
                placeholder="如：顺丰、EMS等"
                style={{ '--font-size': 14 }}
              />
            </div>

            <div style={{ marginBottom: 16 }}>
              <div style={{ fontSize: 14, marginBottom: 4 }}>物流单号（可选）</div>
              <Input
                value={trackingNumber}
                onChange={setTrackingNumber}
                placeholder="输入物流单号"
                style={{ '--font-size': 14 }}
              />
            </div>

            <div style={{ display: 'flex', gap: 12 }}>
              <Button
                block
                variant="outline"
                onClick={() => setShipModalVisible(false)}
              >
                取消
              </Button>
              <Button
                block
                color="primary"
                loading={shipping}
                onClick={handleShip}
              >
                确认发货
              </Button>
            </div>
          </div>
        }
        closeOnMaskClick={true}
        onClose={() => setShipModalVisible(false)}
      />

      {/* 导出报告弹窗 */}
      <Dialog
        visible={exportReportVisible}
        style={{ '--content-width': '90%' }}
        content={
          <div style={{ padding: '16px 0' }}>
            <h3 style={{ fontSize: 16, fontWeight: 600, marginBottom: 16, textAlign: 'center' }}>
              导出订单报告
            </h3>
            <div style={{ marginBottom: 12 }}>
              <div style={{ fontSize: 14, marginBottom: 4 }}>开始日期</div>
              <DatePicker
                value={exportStartDate}
                onChange={setExportStartDate}
                title="选择开始日期"
              >
                {(value) => (
                  <div style={{ padding: '8px 12px', background: '#f5f5f5', borderRadius: 4, fontSize: 14 }}>
                    {dayjs(value).format('YYYY-MM-DD')}
                  </div>
                )}
              </DatePicker>
            </div>
            <div style={{ marginBottom: 16 }}>
              <div style={{ fontSize: 14, marginBottom: 4 }}>结束日期</div>
              <DatePicker
                value={exportEndDate}
                onChange={setExportEndDate}
                title="选择结束日期"
              >
                {(value) => (
                  <div style={{ padding: '8px 12px', background: '#f5f5f5', borderRadius: 4, fontSize: 14 }}>
                    {dayjs(value).format('YYYY-MM-DD')}
                  </div>
                )}
              </DatePicker>
            </div>
            <div style={{ fontSize: 13, color: '#666', marginBottom: 16 }}>
              导出的 CSV 文件将包含订单基本信息及商品 SKU 详情。
            </div>
            <div style={{ display: 'flex', gap: 12 }}>
              <Button
                block
                variant="outline"
                onClick={() => setExportReportVisible(false)}
              >
                取消
              </Button>
              <Button
                block
                color="primary"
                loading={exportReportLoading}
                onClick={handleExportReport}
              >
                导出
              </Button>
            </div>
          </div>
        }
        closeOnMaskClick={true}
        onClose={() => setExportReportVisible(false)}
      />
    </div>
  );
}

// 订单状态标签
function StatusTag({ status }) {
  const statusMap = {
    pending: { text: '待处理', color: '#faad14' },
    paid: { text: '已支付', color: '#52c41a' },
    shipped: { text: '已发货', color: '#1677ff' },
    completed: { text: '已完成', color: '#722ed1' },
    cancelled: { text: '已取消', color: '#ff4d4f' },
    refunded: { text: '已退款', color: '#ff9845' },
  };

  const config = statusMap[status] || { text: status, color: '#999' };

  return (
    <span
      style={{
        fontSize: 12,
        color: config.color,
        background: `${config.color}15`,
        padding: '2px 8px',
        borderRadius: 4,
        display: 'inline-block',
      }}
    >
      {config.text}
    </span>
  );
}

// 财务状态标签
function FinancialStatusTag({ financialStatus }) {
  const statusMap = {
    pending: { text: '待支付', color: '#faad14' },
    paid: { text: '已付款', color: '#52c41a' },
    refunded: { text: '已退款', color: '#ff9845' },
    partially_refunded: { text: '部分退款', color: '#ff7a45' },
    voided: { text: '已作废', color: '#999' },
  };

  if (!financialStatus) return null;
  const config = statusMap[financialStatus] || { text: financialStatus, color: '#999' };

  return (
    <span
      style={{
        fontSize: 12,
        color: config.color,
        background: `${config.color}15`,
        padding: '2px 8px',
        borderRadius: 4,
        display: 'inline-block',
      }}
    >
      {config.text}
    </span>
  );
}

// 订单卡片组件（支持长按发货）
function OrderCard({ order, isDisabled, isChecked, canShip, showSelectMode, onNavigate, onToggleSelect, onOpenShipModal }) {
  const longPressTimer = useRef(null);
  const touchStartPos = useRef({ x: 0, y: 0 });
  const [isLongPressing, setIsLongPressing] = useState(false);
  const [isMoved, setIsMoved] = useState(false);

  const handleTouchStart = (e) => {
    setIsLongPressing(false);
    setIsMoved(false);
    // 记录起始位置
    const touch = e.touches[0];
    touchStartPos.current = { x: touch.clientX, y: touch.clientY };

    longPressTimer.current = setTimeout(() => {
      setIsLongPressing(true);
      if (canShip) {
        onOpenShipModal();
      } else {
        Toast.show('该订单状态不支持发货');
      }
    }, 600);
  };

  const handleTouchMove = (e) => {
    // 检测滑动距离
    const touch = e.touches[0];
    const deltaX = Math.abs(touch.clientX - touchStartPos.current.x);
    const deltaY = Math.abs(touch.clientY - touchStartPos.current.y);

    // 如果滑动超过 10px，判定为滑动而非点击
    if (deltaX > 10 || deltaY > 10) {
      setIsMoved(true);
      // 取消长按计时
      if (longPressTimer.current) {
        clearTimeout(longPressTimer.current);
        longPressTimer.current = null;
      }
    }
  };

  const handleTouchEnd = (e) => {
    // 清除长按计时
    if (longPressTimer.current) {
      clearTimeout(longPressTimer.current);
      longPressTimer.current = null;
    }

    // 如果是长按触发，阻止后续事件
    if (isLongPressing) {
      e.preventDefault();
      return;
    }

    // 如果发生了滑动，不触发点击
    if (isMoved) {
      return;
    }

    // 正常点击进入详情（仅在非多选模式）
    if (!showSelectMode) {
      onNavigate();
    }
  };

  const shipped = order.status === 'shipped';

  return (
    <div
      style={{
        background: shipped ? '#4a4a4a' : (isChecked ? '#e6f4ff' : '#fff'),
        marginBottom: 12,
        padding: '10px 14px',
        borderRadius: 8,
        opacity: isDisabled && showSelectMode ? 0.6 : 1,
        boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
        // 禁用 iOS 文本选择和长按菜单
        WebkitUserSelect: 'none',
        userSelect: 'none',
        WebkitTouchCallout: 'none',
        touchAction: 'pan-y',
      }}
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
      onTouchMove={handleTouchMove}
      onContextMenu={(e) => e.preventDefault()}
      onClick={() => {
        // 点击事件仅在多选模式下用于切换选中状态
        if (showSelectMode && !isDisabled) {
          onToggleSelect();
        }
      }}
    >
      {/* 订单号 + 收件人 */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', WebkitUserSelect: 'none', userSelect: 'none' }}>
        <span style={{ fontSize: 14, fontWeight: 600, color: shipped ? '#ddd' : '#333' }}>{order.orderNo}</span>
        <span style={{ fontSize: 13, fontWeight: 500, color: shipped ? '#bbb' : '#555' }}>
          {[order.shippingLastName, order.shippingFirstName].filter(Boolean).join(' ') || '-'}
        </span>
      </div>

      {/* 邮箱 + 时间 */}
      <div style={{ marginTop: 6, display: 'flex', justifyContent: 'space-between', fontSize: 12, color: shipped ? '#999' : '#999', WebkitUserSelect: 'none', userSelect: 'none' }}>
        <span>{order.email}</span>
        <span>{formatDateTime(order.createdAt)}</span>
      </div>

      {/* 底部：金额 + 状态 */}
      <div style={{ marginTop: 6, display: 'flex', justifyContent: 'space-between', alignItems: 'center', WebkitUserSelect: 'none', userSelect: 'none' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
          <StatusTag status={order.status} />
          {order.financialStatus && order.financialStatus !== order.status && (
            <FinancialStatusTag financialStatus={order.financialStatus} />
          )}
          {canShip && order.status !== 'shipped' && !showSelectMode && (
            <span style={{ fontSize: 11, color: '#52c41a' }}>长按发货</span>
          )}
          {showSelectMode && !isDisabled && (
            <Checkbox
              checked={isChecked}
              onChange={onToggleSelect}
            />
          )}
        </div>
        <div style={{ fontSize: 16, fontWeight: 600, color: shipped ? '#e88' : '#f5222d' }}>
          ${order.total?.toFixed(2) || '0.00'}
        </div>
      </div>
    </div>
  );
}
